import React from "react";
import moment from "moment-timezone";
import { Link, Navigation } from "react-router";
import { capitalize, formatPrice } from "../../util";
import { RaisedButton, MenuItem } from "material-ui";
import TicketList from "./ticket-list/ticket-list.jsx";
import PassList from "./pass-list/pass-list.jsx";
import InjectedTenantHTML from "./injected-tenant-html.jsx";
import InjectedTenantScripts from "./injected-tenant-scripts.jsx";

import CogButtonMenu from "./cog-button-menu.jsx";
import SmsTicketsWidget from "./sms-tickets-widget.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import TicketStore from "../../stores/TicketStore";
import PassStore from "../../stores/PassStore";
import OrderActions from "../../actions/OrderActions";

@connectToStores
class OrderSummary extends React.Component {
  constructor(props) {
    super(props);
  }

  static getStores() {
    return [TicketStore, PassStore];
  }

  static getPropsFromStores() {
    return {
      getPassById: PassStore.getPassById,
      getTicketById: TicketStore.getTicketById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      isLarge: React.PropTypes.bool,
      tenant: React.PropTypes.object,
      appSettings: React.PropTypes.object
    };
  }

  componentWillMount() {
    // Set global order variables for use in injected scripts
    // See https://help.eventive.org/integrations/adding-additional-scripts
    let { order } = this.props;
    window.PERSON_ID = order.person ? order.person.id : "<unknown id>";
    window.PERSON_EMAIL = order.person
      ? order.person.details.email
      : "<unknown email>";
    window.ORDER_ID = order.id;
    window.PURCHASE_PRICE = order.purchase_price / 100;
    window.NET_PRICE = order.net_price / 100;
    window.TICKETS = order.tickets.map(t => {
      return {
        id: t.id,
        event_id: t.event.id,
        event_name: t.event.name,
        ticket_bucket_id: t.ticket_bucket.id,
        ticket_bucket_name: t.ticket_bucket.name,
        price: order._items[t.id].price / 100
      };
    });
    window.PASSES = order.passes.map(p => {
      return {
        id: p.id,
        pass_bucket_id: p.pass_bucket.id,
        pass_bucket_name: p.pass_bucket.name,
        price: order._items[p.id].price / 100
      };
    });
    window.ITEMS = order.items.map(i => {
      return {
        id: i.id,
        item_bucket_id: i.item_bucket.id,
        item_bucket_name: i.item_bucket.name,
        price: order._items[i.id].price / 100
      };
    });
    window.GIFT_CARD_ORDER = order.gift_cards && order.gift_cards.length ? true : false;
  }

  getStyles() {
    return {
      container: {
        textAlign: "left"
      },
      general: {
        textAlign: "left",
        borderTop: "1px solid #ccc",
        paddingTop: 20
      },
      innerContainer: {
        fontSize: "12pt",
        lineHeight: 1.5,
        color: "rgba(0,0,0,0.54)"
      },
      h2: {
        margin: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "rgb(33,33,33)"
      },
      h3: {
        margin: 0
      },
      orderId: {
        textTransform: "uppercase",
        wordWrap: "break-word",
        fontFamily: "monospace",
        letterSpacing: "-1px"
      },
      inOrderSubhead: {
        textAlign: "center",
        color: "rgb(33,33,33)"
      },
      listing: {
        border: "1px solid #ccc",
        marginBottom: "1em"
      },
      actions: {
        marginTop: 20,
        textAlign: "center"
      },
      items: {
        padding: "0"
      },
      error: {
        marginBottom: 10,
        textAlign: "center",
        color: "#FF4136"
      }
    };
  }

  render() {
    let { order, person } = this.props,
      tickets = order.tickets.map(t => this.props.getTicketById(t.id) || t),
      passes = order.passes.map(p => this.props.getPassById(p.id) || p),
      { items } = order,
      isGift = passes.some(p => p.pending_gift_claim_url),
      hasUnfilledData = passes.length
        ? passes.some(
            p => p.needs_supplementary_data && !p.pending_gift_claim_url
          )
        : false,
      hasUnnamedPasses = passes.length
        ? !passes.every(
            p => p.name !== "Unnamed Pass" || p.pending_gift_claim_url
          )
        : false,
      passBucket = passes.length && passes[0].pass_bucket,
      styles = this.getStyles();

    let creationMoment = moment(order.created_at);

    return (
      <div id="Eventive--orderSummary--orderSuccess" style={styles.container}>
        <div style={styles.general}>
          <CogButtonMenu style={{ float: "right" }}>
            <MenuItem
              style={{ cursor: "pointer" }}
              key="download"
              onClick={() => OrderActions.downloadReceipt(order.id)}
              primaryText="Download receipt"
            />
          </CogButtonMenu>
          <h2 style={styles.h2}>
            Order <span style={styles.orderId}>#{order.id}</span>
          </h2>
          <h3 style={styles.h3}>
            {creationMoment.format("LLLL")}
            {this.context.isLarge ? " · " : <br />}
            {formatPrice(order.purchase_price)}
          </h3>
          {order.gift_cards && order.gift_cards.length ?
            (<div style={{ marginTop: 15 }}>
              <InjectedTenantHTML
                content={this.context.tenant.gift_cards_success}
              />
              <div>
                <p>
                  An email has been sent to your gift card recipient. You may also provide them with the following:
                </p>
              </div>
              {order.gift_cards.map((gc) => 
                (<div style={{ marginTop: 15, marginBottom: 10, fontSize: 24 }}>Gift Card Code: <font style={{ fontWeight: "bold" }}>{gc.code}</font></div>)
              )}
            </div>)
          : null}
        </div>
        <div style={styles.innerContainer}>
          {tickets.length ? (
            tickets.every(t => t.event.is_virtual) ? (
              <div style={{ marginTop: "1em" }}>
                Your order has been processed successfully. Check your email for
                your ticket confirmation and event information. When you are
                ready to begin watching your content, click "Watch Now" on the
                screening page.
              </div>
            ) : (
              <div>
                <h3 style={styles.inOrderSubhead}>Tickets in this order</h3>
                <div style={styles.listing}>
                  <TicketList
                    tickets={tickets}
                    hideDateHeaders={true}
                    hideActions={true}
                    showDividers={true}
                  />
                </div>
                {window.EVENTIVE_IS_EVERYWHERE ? (
                  <div>
                    Check your email—a printable copy of these tickets is
                    waiting for you. When it's time for the event, make sure you
                    have your tickets ready to present at the door (printed or
                    electronic).
                  </div>
                ) : order.box_office_email ? (
                  <div>
                    Check your email—a printable copy of these tickets is
                    waiting for you. Since you checked out as a guest, your
                    tickets are not available online. If you have any questions,
                    please contact the box office.
                  </div>
                ) : (
                  <InjectedTenantHTML
                    content={this.context.tenant.order_tickets_success}
                  />
                )}
                {this.context.isLarge && person ? <SmsTicketsWidget /> : null}
              </div>
            )
          ) : null}
          {passes.length ? (
            <div>
              <h3 style={styles.inOrderSubhead}>
                {capitalize(this.context.tenant.display_passes)} in this order
              </h3>
              {hasUnfilledData ? (
                <p style={{ marginTop: "-0.75em" }}>
                  We need a little more information from you before we can
                  complete your order. To begin, click the "edit details" button
                  next to{" "}
                  {passes.length === 1
                    ? `your ${this.context.tenant.display_pass}`
                    : `each of your ${this.context.tenant.display_passes}`}
                  .
                </p>
              ) : hasUnnamedPasses ? (
                <p style={{ marginTop: "-0.75em" }}>
                  First things first, name your{" "}
                  {this.context.tenant.display_passes} by entering a name and
                  then clicking "Save name" next to each{" "}
                  {this.context.tenant.display_pass}. You'll use this name to
                  distinguish between {this.context.tenant.display_passes} when
                  ordering tickets, and it will also be printed on your physical{" "}
                  {this.context.tenant.display_pass}.
                </p>
              ) : null}
              <div style={styles.listing}>
                <PassList
                  passes={passes}
                  onChange={this.onPassChange}
                  hideSettingsLink={true}
                  hideActions={true}
                  initalEditing={!hasUnfilledData && hasUnnamedPasses && true}
                />
              </div>
              {isGift ? (
                <div>
                  Thank you for your gift purchase of {passes.length}{" "}
                  {passes.length === 1
                    ? this.context.tenant.display_pass
                    : this.context.tenant.display_passes}
                  . A PDF containing a gift certificate for each{" "}
                  {this.context.tenant.display_pass} has been emailed to you.
                  You can also use the links above to send the gift certificate
                  directly to your recipient along with a personalized message.
                </div>
              ) : !hasUnfilledData && !hasUnnamedPasses ? (
                <InjectedTenantHTML
                  id="Eventive--orderSummary--passDetailsSubmitted"
                  content={
                    passBucket.confirmation_email_message
                      ? { __html: passBucket.confirmation_email_message }
                      : this.context.tenant.order_passes_success
                  }
                />
              ) : null}
            </div>
          ) : null}
          {items.length &&
          items[0].item_bucket.code === "donation" &&
          !passes.length &&
          !tickets.length ? (
            <div style={styles.items}>
              <h3 style={styles.inOrderSubhead}>Thank you for your donation</h3>
              <InjectedTenantHTML
                content={this.context.tenant.donation_success}
              />
            </div>
          ) : null}
          {order.credits.length ? (
            <div style={styles.items}>
              <h3 style={styles.inOrderSubhead}>
                Thank you for your credit top-up
              </h3>
              <div style={{ textAlign: "center" }}>
                Nice! {order.credits[0].credits_change} credits have been added
                to your account. Enjoy the events!
              </div>
            </div>
          ) : null}
        </div>

        <div style={styles.actions}>
          {hasUnfilledData ? (
            <div style={styles.error}>
              Please submit all required details for your{" "}
              {passes.length !== 1
                ? this.context.tenant.display_passes
                : this.context.tenant.display_pass}{" "}
              before continuing, or{" "}
              <Link to={`/${this.context.tenant.passes_url_pathname}`}>
                save for later
              </Link>
              .
            </div>
          ) : hasUnnamedPasses ? (
            <div style={styles.error}>
              {`Please name your ${
                passes.length !== 1
                  ? this.context.tenant.display_passes
                  : this.context.tenant.display_pass
              } before continuing.`}
            </div>
          ) : null}
          <div style={{ marginTop: -12 }}>
            <RaisedButton
              label="Go back"
              disabled={hasUnfilledData || hasUnnamedPasses}
              onClick={() => {
                this.context.router.goBack();
              }}
              style={{ marginTop: 12 }}
            />
            {!window.EVENTIVE_IS_EVERYWHERE &&
            !window.GIFT_CARD_ORDER &&
            !this.context.router.isActive("/schedule") &&
            this.context.appSettings.schedule_enabled ? (
              <RaisedButton
                label={`Go to ${this.context.tenant.display_schedule}`}
                disabled={hasUnfilledData || hasUnnamedPasses}
                onClick={() => {
                  this.context.router.push("/schedule");
                }}
                style={{ marginTop: 12, marginLeft: 12 }}
              />
            ) : null}
            {!window.EVENTIVE_IS_EVERYWHERE &&
            (passes.length || (tickets.length && !order.box_office_email)) ? (
              <RaisedButton
                label={
                  tickets.length
                    ? "Go to my tickets"
                    : passes.length
                    ? `Go to my ${this.context.tenant.display_passes}`
                    : "Return home"
                }
                disabled={hasUnfilledData || hasUnnamedPasses}
                onClick={() =>
                  this.context.router.push(
                    tickets.length
                      ? "/tickets"
                      : passes.length
                      ? `/${this.context.tenant.passes_url_pathname}`
                      : "/welcome"
                  )
                }
                style={{ marginTop: 12, marginLeft: 12 }}
              />
            ) : null}
          </div>
        </div>

        {this.context.tenant.order_completion_scripts ? (
          <InjectedTenantScripts
            content={this.context.tenant.order_completion_scripts}
          />
        ) : null}
      </div>
    );
  }
}

export default OrderSummary;



// WEBPACK FOOTER //
// ./src/app/components/partials/order-summary.jsx